import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    ChartOptions,
    LinearScale,
    Tooltip,
    ScriptableContext,
} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {useTranslation} from 'react-i18next';
import {
    buildNextProblemPath,
    getLevelName,
    getShortLevelName,
    LEVEL_PROGRESS_BORDER_WIDTH,
} from '@utils';
import {useFetchNextProblemPostMutation, useFetchUserLevelsQuery} from '@api';
import {LevelItem, NextProblemEntryEnum} from '@types';
import generateColor from '@components/levelProgressBar/generateColor';
import clickHandler from '@components/levelProgressBar/clickHandler';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';
import {useGenerateBorderColor} from '@components/hooks/useGenerateBorderColor';
import {useNavigate} from 'react-router';
import {Overlay} from '@goproblems/ui';
import ProgressPopover from '@components/ProgressPopover';
import {useBarChartTooltip} from '@components/hooks/useBarChartTooltip';
import {SPEED_SOLVE_COLOR, SPEED_PLAY_COLOR} from '@utils/const/colors';

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip);

const Y_AXIS_LABEL_GAP = 6;

export type levelProgressProps = {
    userId: number;
    aspectRatio?: number;
    clickable?: boolean;
    maintainAspectRatio?: boolean;
    showGuides?: boolean;
};

export const LevelProgress = ({
    userId,
    aspectRatio = 2,
    clickable = true,
    maintainAspectRatio = true,
    showGuides = false,
}: levelProgressProps) => {
    const navigate = useNavigate();
    const [fetchNextProblem] = useFetchNextProblemPostMutation();
    const {t} = useTranslation();
    const {
        data: levelsData,
        isLoading: isLevelDataLoading,
        isError,
        error,
    } = useFetchUserLevelsQuery(userId);
    const generateBorderColorCallback = useGenerateBorderColor();
    const {activeTooltip, externalTooltip, tooltipTarget} = useBarChartTooltip();

    if (isLevelDataLoading) {
        return (
            <div className={'flex justify-center h-100 align-items-end'}>
                <ThemeAwareSkeleton
                    containerClassName="flex-1"
                    count={5}
                    height={'100%'}
                />
            </div>
        );
    }

    if (isError) {
        if (error && 'status' in error && error.status === 403) {
            return <>{t('level_progress.hidden')}</>;
        }

        return <>{t('common.error')}</>;
    }

    if (!levelsData) {
        return (
            <>
                <ThemeAwareSkeleton />
            </>
        );
    }

    const filteredProgressData = levelsData.filter(
        data => data.level !== null
    ) as LevelItem[];

    const labels = filteredProgressData.map((data: LevelItem) =>
        getLevelName(data.level)
    );
    const progressPercent = (count: number, total: number) =>
        total > 0 ? (count / total) * 100 : 0;
    const levelData = filteredProgressData.map(data =>
        progressPercent(data.solvedCount, data.totalCount)
    );
    const speedLevelData = filteredProgressData.map(data =>
        progressPercent(data.speedSolvedCount, data.totalCount)
    );
    const speedPlayLevelData = filteredProgressData.map(data =>
        progressPercent(data.speedPlayCount, data.totalCount)
    );
    const bgData = levelData.map(d => generateColor(d));
    const borderData = levelData.map((d, index) =>
        generateBorderColorCallback(d, speedLevelData[index])
    );
    const boardWidth = LEVEL_PROGRESS_BORDER_WIDTH;
    const borderWidthData = levelData.map((d, index) => {
        let top = 0,
            left = 0,
            right = 0;

        if (d === 100) {
            top = boardWidth;
            left = boardWidth;
            right = boardWidth;

            if (index > 0 && levelData[index - 1] === 100) {
                left = boardWidth / 2;
            }

            if (index < levelData.length - 1 && levelData[index + 1] === 100) {
                right = boardWidth / 2;
            }
        }

        return {top, left, right};
    });

    const data = {
        labels: labels,
        datasets: [
            {
                categoryPercentage: showGuides ? 0.85 : 1.0,
                barPercentage: 1.0,
                data: levelData,
                backgroundColor: (context: ScriptableContext<'bar'>) => {
                    const chart = context.chart;
                    const {ctx, chartArea} = chart;
                    const index = context.dataIndex;

                    if (!chartArea) {
                        return bgData[index];
                    }

                    const speedSolvePercent = speedLevelData[index];
                    const speedPlayPercent = speedPlayLevelData[index];
                    const totalPercent = levelData[index];
                    const baseColor = bgData[index];

                    if (
                        speedSolvePercent === 0 &&
                        speedPlayPercent === 0 &&
                        totalPercent === 0
                    ) {
                        return baseColor;
                    }

                    const speedSolveRatio = speedSolvePercent / 100;
                    const speedPlayRatio = speedPlayPercent / 100;

                    if (speedSolveRatio === 0 && speedPlayRatio === 0) {
                        return baseColor;
                    }

                    const gradient = ctx.createLinearGradient(
                        0,
                        chartArea.bottom,
                        0,
                        chartArea.top
                    );

                    if (speedSolveRatio > 0) {
                        gradient.addColorStop(0, SPEED_SOLVE_COLOR);
                        gradient.addColorStop(speedSolveRatio, SPEED_SOLVE_COLOR);
                    }

                    if (speedPlayRatio > speedSolveRatio) {
                        gradient.addColorStop(speedSolveRatio, SPEED_PLAY_COLOR);
                        gradient.addColorStop(speedPlayRatio, SPEED_PLAY_COLOR);
                    } else if (speedPlayRatio > 0 && speedSolveRatio === 0) {
                        gradient.addColorStop(0, SPEED_PLAY_COLOR);
                        gradient.addColorStop(speedPlayRatio, SPEED_PLAY_COLOR);
                    }

                    const maxRatio = Math.max(speedSolveRatio, speedPlayRatio);
                    if (maxRatio < 1) {
                        gradient.addColorStop(maxRatio, baseColor);
                        gradient.addColorStop(1, baseColor);
                    }

                    return gradient;
                },
                borderColor: borderData,
                borderWidth: borderWidthData,
            },
        ],
    };

    const openLevelProblem = (level: number) => {
        void (async () => {
            try {
                const result = await fetchNextProblem({
                    name: NextProblemEntryEnum.LEVEL_PROGRESS,
                    rating: level,
                }).unwrap();

                if (result.nextProblem) {
                    navigate(
                        buildNextProblemPath(
                            result.nextProblem.id,
                            result.argument
                        )
                    );
                }
            } catch (error) {
                console.error('Error fetching level progress problem:', error);
            }
        })();
    };

    const options: ChartOptions<'bar'> = {
        maintainAspectRatio,
        onClick: clickable
            ? (event, elements, chart) => {
                  if (!chart.data.labels) return;

                  const rect = chart.canvas.getBoundingClientRect();
                  const x = (event.native as MouseEvent).clientX - rect.left;
                  const y = (event.native as MouseEvent).clientY - rect.top;
                  const canvasArea = chart.chartArea;
                  if (
                      showGuides &&
                      (x < canvasArea.left ||
                          x > canvasArea.right ||
                          y < canvasArea.top ||
                          y > canvasArea.bottom)
                  ) {
                      return;
                  }
                  const barWidth = canvasArea.width / chart.data.labels.length;

                  const columnIndex = Math.floor(
                      (x - canvasArea.left) / barWidth
                  );

                  if (
                      columnIndex >= 0 &&
                      columnIndex < chart.data.labels.length
                  ) {
                      const mockElements = [
                          {
                              index: columnIndex,
                              datasetIndex: 0,
                              element: {} as BarElement,
                          },
                      ];
                      clickHandler(
                          event,
                          mockElements,
                          chart,
                          navigate,
                          openLevelProblem
                      );
                  }
              }
            : undefined,
        aspectRatio,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            legend: {display: false},
            tooltip: {
                enabled: false,
                external: externalTooltip,
                intersect: false,
                mode: 'index',
            },
        },
        scales: {
            y: {
                afterFit: scale => {
                    if (showGuides) {
                        // Chart.js pads both sides; keep only the gap to the bars.
                        scale.width -= Y_AXIS_LABEL_GAP;
                    }
                },
                grid: {
                    display: showGuides,
                    drawTicks: false,
                    color: context =>
                        getComputedStyle(context.chart.canvas).borderTopColor,
                },
                border: {display: false, dash: [3, 3]},
                ticks: {
                    display: showGuides,
                    stepSize: showGuides ? 50 : undefined,
                    autoSkip: false,
                    padding: Y_AXIS_LABEL_GAP,
                    font: {size: 10},
                    color: context =>
                        getComputedStyle(context.chart.canvas).color,
                    callback: value => `${value}%`,
                },
                max: showGuides ? 100 : undefined,
                suggestedMax: 100,
                beginAtZero: true,
            },
            x: {
                grid: {display: false, drawTicks: false},
                border: {display: !showGuides, width: 0.5},
                ticks: {
                    display: showGuides,
                    autoSkip: false,
                    maxRotation: 0,
                    padding: 4,
                    font: {size: 10},
                    color: context =>
                        getComputedStyle(context.chart.canvas).color,
                    callback: value => {
                        const index = Number(value);
                        const level = filteredProgressData[index]?.level;
                        const isMilestone = level % 10 === 0;
                        const isLast = index === filteredProgressData.length - 1;
                        return level !== undefined && (isMilestone || isLast)
                            ? getShortLevelName(level)
                            : null;
                    },
                },
            },
        },
    };

    const tooltipData = activeTooltip
        ? filteredProgressData[activeTooltip.dataIndex]
        : undefined;
    const percentage = (count: number) =>
        ((count / (tooltipData?.totalCount || 1)) * 100).toFixed(1);
    const chart = (
        <Bar
            data={data}
            options={options}
            className="border-neutral-200 text-neutral-500 dark:border-neutral-700 dark:text-neutral-400"
            role="img"
            aria-label={t('index.level_progress')}
        />
    );

    return (
        <>
            {showGuides && (
                <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs leading-4 text-neutral-500 dark:text-neutral-400">
                    {[
                        {label: t('levels.solved'), color: generateColor(50)},
                        {label: t('levels.speed_play'), color: SPEED_PLAY_COLOR},
                        {label: t('levels.speed_solve'), color: SPEED_SOLVE_COLOR},
                    ].map(item => (
                        <span
                            key={item.label}
                            className="inline-flex items-center gap-1.5">
                            <span
                                aria-hidden="true"
                                className="size-2 shrink-0 rounded-sm"
                                style={{backgroundColor: item.color}}
                            />
                            {item.label}
                        </span>
                    ))}
                </div>
            )}
            {showGuides ? (
                <div className="relative min-h-0 flex-1">{chart}</div>
            ) : chart}
            {tooltipData && (
                <Overlay
                    show
                    placement="top"
                    target={tooltipTarget}
                    className="pointer-events-none">
                    <ProgressPopover
                        className="w-60"
                        title={getLevelName(tooltipData.level)}
                        metrics={[
                            {
                                label: t('levels.progress'),
                                value: `${percentage(tooltipData.solvedCount)}%`,
                            },
                            {
                                label: t('levels.problems'),
                                value: tooltipData.totalCount,
                            },
                            {
                                label: t('levels.solved'),
                                value: tooltipData.solvedCount,
                            },
                            {
                                label: t('levels.speed_solve'),
                                value: `${tooltipData.speedSolvedCount} (${percentage(tooltipData.speedSolvedCount)}%)`,
                            },
                            {
                                label: t('levels.speed_play'),
                                value: `${tooltipData.speedPlayCount} (${percentage(tooltipData.speedPlayCount)}%)`,
                            },
                        ]}
                    />
                </Overlay>
            )}
        </>
    );
};
